// src/pages/jobs/JobDashboard.jsx
// === Corporate Clean v2 — refreshed UI, exact same logic & features ===
// - No new features, no reductions
// - API calls / routes unchanged
// - Role palettes retained
// - Settings icon removed globally (per instruction)

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Divider,
  Container,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
const ROWS_PER_PAGE = 10;

// ===== Role-based palette =====
const PALETTES = {
  admin: {
    primary: "#263238",
    secondary: "#26A69A",
    chipText: "#fff",
    bg: "#F4F6F8",
    text: "#1A1A1A",
  },
  engineer: {
    primary: "#003366",
    secondary: "#FFD700",
    chipText: "#000",
    bg: "#F5F7FA",
    text: "#1A1A1A",
  },
};
const usePalette = (role) =>
  String(role || "").toLowerCase() === "admin" ? PALETTES.admin : PALETTES.engineer;

function DaysLeftChip({ value }) {
  if (value === null || value === undefined) return <Chip size="small" variant="outlined" label="—" />;
  let color = "success";
  let label = `${value} days`;
  if (value <= 7 && value >= 1) color = "warning";
  if (value === 0) { color = "error"; label = "Due Today"; }
  if (value < 0)  { color = "error"; label = `Overdue (${Math.abs(value)} days)`; }
  return <Chip size="small" color={color} label={label} />;
}

// Feature 1: priority levels on issues
function PriorityChip({ value }) {
  const v = String(value || "medium").toLowerCase();
  const map = {
    critical: { label: "Critical", color: "error" },
    high: { label: "High", color: "warning" },
    medium: { label: "Medium", color: "info" },
    low: { label: "Low", color: "default" },
  };
  const conf = map[v] || map.medium;
  return <Chip size="small" label={conf.label} color={conf.color} sx={{ fontWeight: 700 }} />;
}

// Feature 6: SLA aging indicator — same visual language as DaysLeftChip,
// but for "how long has this been open" instead of "days until due".
function IssueAgeChip({ days }) {
  if (days === null || days === undefined) return <Chip size="small" variant="outlined" label="—" />;
  let color = "success";
  let label = days === 0 ? "Opened today" : `${days}d open`;
  if (days >= 2 && days < 5) color = "warning";
  if (days >= 5) color = "error";
  return <Chip size="small" color={color} label={label} />;
}

function formatUTC(isoUTC) {
  if (!isoUTC) return "—";
  try {
    const d = new Date(isoUTC);
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return isoUTC;
  }
}

function StatusChip({ value }) {
  const v = String(value || "").toLowerCase();
  const map = {
    ongoing: { label: "Ongoing", color: "info" },
    completed: { label: "Completed", color: "success" },
    cancelled: { label: "Cancelled", color: "default" },
  };
  const conf = map[v] || { label: value || "—", color: "default" };
  return <Chip size="small" label={conf.label} color={conf.color} />;
}

const CARD_DEFS = (palette) => [
  { key: "totalJobs", label: "Total Jobs", icon: <WorkHistoryIcon />, color: palette.primary },
  { key: "ongoing", label: "Ongoing Jobs", icon: <BuildCircleIcon />, color: palette.secondary },
  { key: "completed", label: "Completed", icon: <CheckCircleIcon />, color: "#4CAF50" },
  { key: "dueMaint", label: "Due Maintenance", icon: <AccessTimeIcon />, color: "#FF9800" },
  { key: "jobsThisMonth", label: "Jobs This Month", icon: <CalendarMonthIcon />, color: "#7E57C2" },
  { key: "openIssues", label: "Open Issues", icon: <ErrorOutlineIcon />, color: "#E53935" },
];




export default function JobDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "engineer").toLowerCase();
  const palette = usePalette(role);

  // ===== State =====
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summary, setSummary] = useState(null);

  const [loadingRecent, setLoadingRecent] = useState(true);
  const [recentRows, setRecentRows] = useState([]);

  const [loadingDue, setLoadingDue] = useState(true);
  const [dueRows, setDueRows] = useState([]);

  const [search, setSearch] = useState("");

  // Open Issues panel — new
  const [loadingIssues, setLoadingIssues] = useState(true);

  // Feature 1: combined maintenance/resolved/unresolved PDF report
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [generatingReport, setGeneratingReport] = useState(false);
  const [openIssues, setOpenIssues] = useState([]);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");

  const token = localStorage.getItem("token");

  // ===== Quick Job Assignment (admin only) =====
  // A preliminary, minimal job created directly from the dashboard: pick
  // a title/client/tier, assign an engineer, and it auto-creates a
  // "confirm details" issue on that job — reusing the existing Open
  // Issues panel/Mark Resolved flow as the "engineer accepts this" step,
  // rather than building a whole separate draft/convert system.
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [quickTitle, setQuickTitle] = useState("");
  const [quickClient, setQuickClient] = useState("");
  const [quickJobType, setQuickJobType] = useState("Installation");
  const [quickLocation, setQuickLocation] = useState("");
  const [quickAssignees, setQuickAssignees] = useState([]);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState("");
  const [quickSuccess, setQuickSuccess] = useState("");

  useEffect(() => {
    if (role !== "admin") return;
    fetch(`${API_BASE}/api/jobs/issues/assignable-users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setAssignableUsers(data?.users || []))
      .catch((e) => console.error("Failed to load assignable users:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const toggleQuickAssignee = (u) => {
    setQuickAssignees((prev) =>
      prev.some((x) => x.id === u.id) ? prev.filter((x) => x.id !== u.id) : [...prev, u]
    );
  };

  const submitQuickAssign = async () => {
    setQuickError("");
    setQuickSuccess("");

    if (!quickTitle.trim() || !quickClient.trim()) {
      setQuickError("Title and Client are required.");
      return;
    }
    if (!quickAssignees.length) {
      setQuickError("Assign at least one engineer.");
      return;
    }

    setQuickSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/jobs/quick-assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: quickTitle.trim(),
          job_type: quickJobType,
          client_name: quickClient.trim(),
          location: quickLocation.trim() || null,
          user_ids: quickAssignees.map((u) => u.id),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to create and assign job");

      setQuickSuccess(`Job ${data.job.job_ref} created and assigned.`);
      setQuickTitle("");
      setQuickClient("");
      setQuickJobType("Installation");
      setQuickLocation("");
      setQuickAssignees([]);
      fetchRecentJobs();
      fetchSummary();
      fetchOpenIssues();
    } catch (e) {
      setQuickError(e.message || "Failed to create and assign job");
    } finally {
      setQuickSaving(false);
    }
  };

  // ===== Fetchers =====
  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/summary`);
      const data = await res.json();
      setSummary(data || { cards: {} });
    } catch (e) {
      console.error(e);
      setSummary({ cards: {} });
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchRecentJobs = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/recent-jobs`);
      const json = await res.json();
      const rows = (json?.jobs || []).map((r, i) => ({
        id: r.job_id || i,
        job_id: r.job_id,
        job_ref: r.job_ref,
        title: r.title,
        client_name: r.client_name,
        status: r.status ?? "ongoing",
        created_at_fmt: r.created_at_fmt,
      }));
      setRecentRows(rows);
    } catch (e) {
      console.error(e);
      setRecentRows([]);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  const fetchDueMaintenance = useCallback(async () => {
    setLoadingDue(true);
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/due-maintenance`);
      const json = await res.json();
      const dueRowsMapped = (json?.items || []).map((r, i) => ({
        id: r.job_id || i,
        job_id: r.job_id,
        job_ref: r.job_ref,
        title: r.title,
        client_name: r.client_name,
        last_maint_fmt: r.last_maint_fmt,
        next_maint_fmt: r.next_maint_fmt,
        days_left: r.days_left,
      }));
      setDueRows(dueRowsMapped);
    } catch (e) {
      console.error(e);
      setDueRows([]);
    } finally {
      setLoadingDue(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchRecentJobs();
    fetchDueMaintenance();
    fetchOpenIssues();
  }, [fetchSummary, fetchRecentJobs, fetchDueMaintenance]);

  const fetchOpenIssues = useCallback(async () => {
    setLoadingIssues(true);
    try {
      const res = await fetch(`${API_BASE}/api/jobs/issues/open?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setOpenIssues(json?.issues || []);
    } catch (e) {
      console.error(e);
      setOpenIssues([]);
    } finally {
      setLoadingIssues(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openResolveDialog = (issue) => {
    setResolveTarget(issue);
    setResolutionNote("");
    setResolveError("");
  };

  const submitResolve = async () => {
    if (!resolveTarget) return;
    if (!resolutionNote.trim()) {
      setResolveError("A resolution note is required to mark this resolved.");
      return;
    }

    setResolving(true);
    setResolveError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/jobs/${resolveTarget.job_id}/issues/${resolveTarget.issue_id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: "resolved", resolution_note: resolutionNote.trim() }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to resolve issue");

      setResolveTarget(null);
      fetchOpenIssues();
      fetchSummary(); // "Open Issues" KPI card count should drop too
    } catch (e) {
      setResolveError(e.message || "Failed to resolve issue");
    } finally {
      setResolving(false);
    }
  };

  // ===== Feature 1: combined maintenance/resolved/unresolved PDF =====
  // Pulls GET /api/jobs/reports/combined for the chosen range and builds
  // one PDF with three sections, rather than three separate downloads.
  const generateCombinedReport = async (range) => {
    setGeneratingReport(true);
    try {
      const qs = new URLSearchParams();
      if (range === "custom") {
        qs.set("date", reportDate);
      } else {
        qs.set("range", range);
      }

      const res = await fetch(`${API_BASE}/api/jobs/reports/combined?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to build report");

      const { default: jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const marginX = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      let cursorY = 0;

      // Priority → RGB, reused for both header accents and cell text
      const PRIORITY_COLOR = {
        critical: [211, 47, 47],
        high: [239, 108, 0],
        medium: [25, 118, 210],
        low: [117, 117, 117],
      };
      const priorityColor = (p) => PRIORITY_COLOR[String(p || "medium").toLowerCase()] || PRIORITY_COLOR.medium;

      // ---- Branded header band ----
      doc.setFillColor(7, 27, 54); // matches the app's dark navy accent
      doc.rect(0, 0, pageWidth, 70, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.text("SFV TECH", marginX, 30);
      doc.setFontSize(12);
      doc.text(
        `Operations Report — ${data.range.start === data.range.end ? data.range.start : `${data.range.start} to ${data.range.end}`}`,
        marginX,
        50
      );
      doc.setTextColor(0, 0, 0);
      cursorY = 95;

      // ---- Summary stat bar ----
      const stats = [
        { label: "Maintenance Done", value: data.maintenance.length, color: [76, 175, 80] },
        { label: "Resolved Issues", value: data.resolvedIssues.length, color: [46, 125, 50] },
        { label: "Unresolved Issues", value: data.unresolvedIssues.length, color: [211, 47, 47] },
      ];
      const boxWidth = (pageWidth - marginX * 2 - 20) / 3;
      stats.forEach((s, idx) => {
        const boxX = marginX + idx * (boxWidth + 10);
        doc.setFillColor(...s.color);
        doc.roundedRect(boxX, cursorY, boxWidth, 45, 4, 4, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.text(String(s.value), boxX + 12, cursorY + 20);
        doc.setFontSize(9);
        doc.text(s.label, boxX + 12, cursorY + 35);
        doc.setTextColor(0, 0, 0);
      });
      cursorY += 65;

      const newPageIfNeeded = (needed = 100) => {
        if (cursorY > 780 - needed) {
          doc.addPage();
          cursorY = 45;
        }
      };

      // Breaks a comma-joined assignee list into lines of 2 names each,
      // so a long assignee list doesn't force one row to wrap mid-list
      // and stretch taller than every other row in the table.
      const formatAssignees = (name) => {
        if (!name) return "Unassigned";
        const names = name.split(",").map((n) => n.trim()).filter(Boolean);
        const lines = [];
        for (let i = 0; i < names.length; i += 2) {
          lines.push(names.slice(i, i + 2).join(", "));
        }
        return lines.join("\n");
      };

      // Colors the Priority column's text by severity, in any table that
      // has a "Priority" header — used via didParseCell below.
      const colorPriorityCell = (hookData, priorityColIndex) => {
        if (hookData.section === "body" && hookData.column.index === priorityColIndex) {
          const [r, g, b] = priorityColor(hookData.cell.raw);
          hookData.cell.styles.textColor = [r, g, b];
          hookData.cell.styles.fontStyle = "bold";
        }
      };

      // Page-number footer on every page.
      const drawFooter = (hookData) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Generated ${new Date().toLocaleString()}`,
          marginX,
          doc.internal.pageSize.getHeight() - 20
        );
        doc.text(
          `Page ${hookData.pageNumber} of ${pageCount}`,
          pageWidth - marginX - 60,
          doc.internal.pageSize.getHeight() - 20
        );
        doc.setTextColor(0, 0, 0);
      };

      // Draws a small amber note under a section header if that
      // section hit the backend's row cap — so a huge date range
      // shows an honest "showing first N of possibly more" instead of
      // silently looking like a complete list.
      const drawTruncatedNote = (isTruncated, cap) => {
        if (!isTruncated) return;
        doc.setFontSize(9);
        doc.setTextColor(230, 126, 0);
        doc.text(`Showing first ${cap} rows — refine the date range for a complete list.`, marginX, cursorY);
        doc.setTextColor(0, 0, 0);
        cursorY += 14;
      };

      // ---- Maintenance Done ----
      doc.setFontSize(13);
      doc.text(`Maintenance Done (${data.maintenance.length})`, marginX, cursorY);
      cursorY += 8;
      drawTruncatedNote(data.truncated?.maintenance, data.rowCap);
      if (data.maintenance.length) {
        autoTable(doc, {
          startY: cursorY + 6,
          head: [["Job Ref", "Client", "Date/Time", "Performed By", "Team", "Description", "Materials Used"]],
          body: data.maintenance.map((m) => [
            m.job_ref, m.client_name || "—", m.date_time || "—", m.performed_by || "—",
            m.team_members || "—", m.description || "—", m.materials_used || "—",
          ]),
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [76, 175, 80] },
          alternateRowStyles: { fillColor: [245, 250, 245] },
          margin: { left: marginX, right: marginX },
          didDrawPage: drawFooter,
        });
        cursorY = doc.lastAutoTable.finalY + 25;
      } else {
        cursorY += 20;
        doc.setFontSize(10);
        doc.text("No maintenance recorded in this range.", marginX, cursorY);
        cursorY += 25;
      }

      // ---- Resolved Issues ----
      // Column order per request: Job Ref, Client, Issue, Priority,
      // Assign To, Opened — then Resolved/Resolution Note appended.
      newPageIfNeeded(150);
      doc.setFontSize(13);
      doc.text(`Resolved Issues (${data.resolvedIssues.length})`, marginX, cursorY);
      cursorY += 8;
      drawTruncatedNote(data.truncated?.resolvedIssues, data.rowCap);
      if (data.resolvedIssues.length) {
        autoTable(doc, {
          startY: cursorY + 6,
          head: [["Job Ref", "Client", "Issue", "Description", "Priority", "Assign To", "Opened", "Resolved", "Resolution Note"]],
          body: data.resolvedIssues.map((i) => [
            i.job_ref, i.client_name || "—", i.issue_title, i.description || "—", (i.priority || "medium").toUpperCase(),
            formatAssignees(i.assigned_to_name), i.opened_at_utc || "—",
            i.resolved_at_utc || "—", i.resolution_note || "—",
          ]),
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [46, 125, 50] },
          alternateRowStyles: { fillColor: [245, 250, 245] },
          margin: { left: marginX, right: marginX },
          didParseCell: (d) => colorPriorityCell(d, 4),
          didDrawPage: drawFooter,
        });
        cursorY = doc.lastAutoTable.finalY + 25;
      } else {
        cursorY += 20;
        doc.setFontSize(10);
        doc.text("No issues resolved in this range.", marginX, cursorY);
        cursorY += 25;
      }

      // ---- Unresolved Issues (always current, not date-scoped) ----
      newPageIfNeeded(150);
      doc.setFontSize(13);
      doc.text(`Unresolved Issues — Current (${data.unresolvedIssues.length})`, marginX, cursorY);
      cursorY += 8;
      drawTruncatedNote(data.truncated?.unresolvedIssues, data.rowCap);
      if (data.unresolvedIssues.length) {
        autoTable(doc, {
          startY: cursorY + 6,
          head: [["Job Ref", "Client", "Issue", "Description", "Priority", "Assign To", "Opened"]],
          body: data.unresolvedIssues.map((i) => [
            i.job_ref, i.client_name || "—", i.issue_title, i.description || "—", (i.priority || "medium").toUpperCase(),
            formatAssignees(i.assigned_to_name), i.opened_at_utc || "—",
          ]),
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [211, 47, 47] },
          alternateRowStyles: { fillColor: [253, 245, 245] },
          margin: { left: marginX, right: marginX },
          didParseCell: (d) => colorPriorityCell(d, 4),
          didDrawPage: drawFooter,
        });
      } else {
        cursorY += 20;
        doc.setFontSize(10);
        doc.text("No unresolved issues right now.", marginX, cursorY);
      }

      doc.save(`sfvtech_operations_report_${data.range.start}${data.range.start !== data.range.end ? `_to_${data.range.end}` : ""}.pdf`);
    } catch (err) {
      console.error("Combined report error:", err);
      alert("Failed to generate the report. Please try again.");
    } finally {
      setGeneratingReport(false);
    }
  };

  // ===== Search Filters =====
  const searchLower = (search || "").toLowerCase();
  const filteredRecent = useMemo(
    () =>
      recentRows.filter(
        (r) =>
          r.job_ref?.toLowerCase().includes(searchLower) ||
          r.title?.toLowerCase().includes(searchLower) ||
          r.client_name?.toLowerCase().includes(searchLower)
      ),
    [recentRows, searchLower]
  );

  const filteredDue = useMemo(
    () =>
      dueRows.filter(
        (r) =>
          r.job_ref?.toLowerCase().includes(searchLower) ||
          r.title?.toLowerCase().includes(searchLower) ||
          r.client_name?.toLowerCase().includes(searchLower)
      ),
    [dueRows, searchLower]
  );

  const cardDefs = useMemo(() => CARD_DEFS(palette), [palette]);

 

  const handleCardClick = (key) => {
    // Every card now deep-links to a real filter on ManageWorkPage —
    // previously "Total Jobs", "Due Maintenance", "Jobs This Month", and
    // "Open Issues" all silently opened the same unfiltered list.
    const map = {
      totalJobs: "",
      ongoing: "status=ongoing",
      completed: "status=completed",
      dueMaint: "due_maintenance=1",
      jobsThisMonth: "created_this_month=1",
      openIssues: "has_open_issues=1",
    };
    const qs = map[key] ? `?${map[key]}` : "";
    navigate(`/jobs/manage${qs}`);
  };

  // ===== Table Columns =====
  const recentCols = [
    { field: "job_ref", headerName: "Job Ref", flex: 1, minWidth: 130 },
    { field: "title", headerName: "Title", flex: 1.4, minWidth: 180 },
    { field: "client_name", headerName: "Client", flex: 1, minWidth: 140 },
    { field: "status", headerName: "Status", flex: 0.8, minWidth: 120, renderCell: (p) => <StatusChip value={p.value} /> },
    { field: "created_at_fmt", headerName: "Start Date", flex: 1, minWidth: 180 },
  ];

  const dueCols = [
    { field: "job_ref", headerName: "Job Ref", flex: 1.2, minWidth: 150 },
    { field: "title", headerName: "Title", flex: 1.4, minWidth: 160 },
    { field: "client_name", headerName: "Client", flex: 1.2, minWidth: 150 },
    { field: "last_maint_fmt", headerName: "Last Maintenance", flex: 1.3, minWidth: 190 },
    { field: "next_maint_fmt", headerName: "Next Maintenance", flex: 1.1, minWidth: 150 },
    { field: "days_left", headerName: "Days Left", flex: 0.9, minWidth: 130, sortable: true, renderCell: (p) => <DaysLeftChip value={p?.value} /> },
  ];

  // ===== Styles =====
  const cardSx = {
    cursor: "pointer",
    borderRadius: 3,
    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    transition: "transform 180ms ease, box-shadow 180ms ease",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 24px rgba(0,0,0,0.10)" },
  };

  const panelHeaderSx = {
    px: 2, pt: 2, pb: 1.25,
    borderBottom: "1px solid #eee",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  };

  const gridSx = {
    "& .MuiDataGrid-columnHeaders": { height: 46, minHeight: 46 },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: 13 },
    "& .MuiDataGrid-cell": { fontSize: 13 },
    "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
    "& .MuiDataGrid-footerContainer": { borderTop: "none" },
  };

  return (
    <Box sx={{ background: palette.bg, minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: palette.primary,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 0.2 }}>
            SFV Tech | Job Management
          </Typography>

          <TextField
            size="small"
            placeholder="Search (Ref / Title / Client)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mr: 2,
              background: "#ffffff",
              borderRadius: 1.5,
              width: 320,
              display: { xs: "none", sm: "block" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

       

          {/* Settings removed on request */}

          <Chip
            icon={<PersonIcon />}
            label={String(role).toUpperCase()}
            size="small"
            sx={{
              ml: 1,
              bgcolor: palette.secondary,
              color: palette.chipText,
              fontWeight: 700,
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        {/* Top filter on mobile */}
        <Box sx={{ display: { xs: "block", sm: "none" }, mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search (Ref / Title / Client)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2}>
          {cardDefs.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.key}>
              <motion.div whileHover={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 250 }}>
                <Card onClick={() => handleCardClick(c.key)} sx={cardSx}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.75,
                          display: "grid",
                          placeItems: "center",
                          background: c.color,
                          color: "#fff",
                        }}
                      >
                        {c.icon}
                      </Box>
                      {loadingSummary ? (
                        <Skeleton variant="text" width={60} height={28} />
                      ) : (
                        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
                          {summary?.cards?.[c.key] ?? 0}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="subtitle2" sx={{ mt: 1.25, color: palette.text, fontWeight: 700 }}>
                      {c.label}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Section Divider */}
        <Divider sx={{ my: 2.5 }} />

        {/* Tables */}
        <Grid container spacing={2}>
          {/* Recent Jobs */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
              <Box sx={panelHeaderSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Recent Jobs
                </Typography>
                <Button size="small" onClick={() => navigate("/jobs/manage")}>View All</Button>
              </Box>
              <div style={{ height: 430, width: "100%" }}>
                <DataGrid
                  rows={filteredRecent.slice(0, ROWS_PER_PAGE)}
                  columns={recentCols}
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  paginationModel={{ pageSize: ROWS_PER_PAGE, page: 0 }}
                  disableRowSelectionOnClick
                  loading={loadingRecent}
                  onRowClick={(p) => navigate(`/jobs/${p.row.job_id}`)}
                  sx={gridSx}
                />
              </div>
            </Card>
          </Grid>

          {/* Due Maintenance */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
              <Box sx={panelHeaderSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Due Maintenance (In 2 weeks)
                </Typography>
                <Button size="small" onClick={() => navigate("/jobs/manage?due_maintenance=1")}>View Due</Button>
              </Box>
              <div style={{ height: 430, width: "100%" }}>
                <DataGrid
                  rows={filteredDue.slice(0, ROWS_PER_PAGE)}
                  columns={dueCols}
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  paginationModel={{ pageSize: ROWS_PER_PAGE, page: 0 }}
                  disableRowSelectionOnClick
                  loading={loadingDue}
                  onRowClick={(p) => navigate(`/jobs/${p.row.job_id}`)}
                  sx={gridSx}
                />
              </div>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Job Assignment (admin only) */}
        {role === "admin" && (
          <Card sx={{ mt: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
            <Box sx={panelHeaderSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                Quick Job Assignment
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
                Create a minimal preliminary job and assign an engineer right away — they'll see it on
                their dashboard and get a "Confirm Details" issue to resolve once the job is fully
                filled in (location, materials, etc. via Edit Job).
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Job Title"
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Client Name"
                    value={quickClient}
                    onChange={(e) => setQuickClient(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Location"
                    value={quickLocation}
                    onChange={(e) => setQuickLocation(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Job Type"
                    value={quickJobType}
                    onChange={(e) => setQuickJobType(e.target.value)}
                  >
                    {["Installation", "Repair", "Maintenance", "Inspection", "Others"].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Typography variant="body2" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>
                Assign To
              </Typography>
              <Box sx={{ maxHeight: 180, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                {assignableUsers.map((u) => {
                  const checked = quickAssignees.some((sel) => sel.id === u.id);
                  return (
                    <FormControlLabel
                      key={u.id}
                      sx={{ display: "flex" }}
                      control={<Checkbox checked={checked} onChange={() => toggleQuickAssignee(u)} size="small" />}
                      label={`${u.name} (${u.role})${u.job_count > 0 ? ` — ${u.job_count} job(s) already` : ""}`}
                    />
                  );
                })}
                {assignableUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No users found.
                  </Typography>
                )}
              </Box>

              {quickError && <Alert severity="error" sx={{ mt: 2 }}>{quickError}</Alert>}
              {quickSuccess && <Alert severity="success" sx={{ mt: 2 }}>{quickSuccess}</Alert>}

              <Button
                variant="contained"
                startIcon={quickSaving ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                disabled={quickSaving}
                onClick={submitQuickAssign}
                sx={{ mt: 2 }}
              >
                {quickSaving ? "Creating..." : "Create & Assign"}
              </Button>
            </Box>
          </Card>
        )}

        {/* Open Issues Panel — new */}
        <Card sx={{ mt: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
              Open Issues ({openIssues.length})
            </Typography>
            <Button size="small" onClick={() => navigate("/jobs/manage?has_open_issues=1")}>
              View All
            </Button>
          </Box>

          {loadingIssues ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : openIssues.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", opacity: 0.6 }}>
              No open issues — everything's resolved.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
              {openIssues.map((issue) => (
                <Box
                  key={issue.issue_id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f1f1f1",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700 }}>{issue.title}</Typography>
                      <PriorityChip value={issue.priority} />
                      <IssueAgeChip days={issue.days_open} />
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {issue.job_ref} · {issue.client_name || "No client"}
                      {issue.assigned_to_name ? ` · Assigned: ${issue.assigned_to_name}` : " · Unassigned"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.25 }}>
                      {issue.description}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.5 }}>
                      Opened {issue.opened_at_utc} (UTC)
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/jobs/${issue.job_id}`)}>
                      View Job
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => openResolveDialog(issue)}
                    >
                      Mark Resolved
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* Feature 1: Combined Operations Report (PDF) */}
        <Card sx={{ mt: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
              Operations Report (PDF)
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
              One PDF covering maintenance done, resolved issues, and every
              currently unresolved issue across all jobs.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={generatingReport ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                disabled={generatingReport}
                onClick={() => generateCombinedReport("today")}
              >
                Today
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={generatingReport ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                disabled={generatingReport}
                onClick={() => generateCombinedReport("yesterday")}
              >
                Yesterday
              </Button>
              <TextField
                type="date"
                size="small"
                label="Custom date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                startIcon={generatingReport ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                disabled={generatingReport}
                onClick={() => generateCombinedReport("custom")}
              >
                Download for Date
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Quick Actions */}
        <Box sx={{ mt: 2.25, display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            sx={{ bgcolor: palette.secondary, color: palette.chipText, "&:hover": { opacity: 0.9 } }}
            onClick={() => navigate("/jobs/register")}
          >
            Register New Work
          </Button>
          <Button variant="outlined" onClick={() => navigate("/jobs/manage")}>
            View All Jobs
          </Button>
          
        </Box>
      </Container>

      {/* Resolve Issue Dialog */}
      <Dialog open={Boolean(resolveTarget)} onClose={() => setResolveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Issue Resolved</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            <b>{resolveTarget?.title}</b> — {resolveTarget?.job_ref}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Resolution Note"
            placeholder="What was done to resolve this?"
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
          {resolveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {resolveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveTarget(null)} disabled={resolving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={resolving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            onClick={submitResolve}
            disabled={resolving}
          >
            {resolving ? "Resolving..." : "Confirm Resolved"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
